import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useFormStore } from '@store/use-form-store';

import checkboxGreen from '../../assets/checkboxGreen.svg';
import checkboxGreenChecked from '../../assets/checkboxGreenChecked.svg';
import checkboxRed from '../../assets/checkboxRed.svg';
import checkboxRedChecked from '../../assets/checkboxRedChecked.svg';
import companyIcon from '../../assets/companyIcon.svg';
import { useVismaConfiguration } from '../../services/visitor-management';
import type { Registrant } from '../../types';

const Container = styled.div`
  background: ${({ theme }) => theme.white};
  margin-bottom: 16px;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
`;

const Name = styled.h4`
  font-weight: 600;
  font-size: 18px;
  color: ${({ theme }) => theme.text};
  margin-bottom: 8px;
`;

const LineContainer = styled.div`
  display: flex;
  justify-content: space-between;
  & > * {
    width: 50%;
  }
`;

const CompanyContainer = styled.div`
  display: flex;
  align-items: center;
  & > img {
    margin-right: 8px;
  }
`;

const CompanyName = styled.span`
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.textSecondary};
`;

interface CheckboxContainerProps {
  isVideoWatched?: boolean;
}

const CheckboxContainer = styled.div<CheckboxContainerProps>`
  display: flex;
  align-items: center;
  margin-top: 8px;
  opacity: ${(props) => (props.isVideoWatched ? 1 : 0.5)};
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  & > img {
    cursor: pointer;
    margin-right: 4px;
  }
  &:last-child {
    margin-left: 16px;
  }
`;

interface RegistrantVideoProps {
  registrant: Registrant;
}

function RegistrantVideo({ registrant }: RegistrantVideoProps) {
  const intl = useIntl();
  const { isVideoWatched, changeRegistrantVideoStatus } = useFormStore();
  const { configuration } = useVismaConfiguration();
  const dataField = configuration?.personalData?.data_field;

  const handleCheckbox = (type: string) => {
    if (!isVideoWatched) return;
    changeRegistrantVideoStatus({
      registrantDocNo: registrant.docNo,
      videoStatus: type
    });
  };

  return (
    <Container>
      <LineContainer>
        <Name>{registrant.fullname}</Name>
        <CompanyName>{intl.formatMessage({ id: 'VIDEO_TERMS' })}</CompanyName>
      </LineContainer>
      <LineContainer>
        {registrant.companyName && !dataField?.company_name?.disabled ? (
          <CompanyContainer>
            <img src={companyIcon} alt="company" height={24} width={24} />
            <CompanyName>{registrant.companyName}</CompanyName>
          </CompanyContainer>
        ) : (
          <div></div>
        )}
        <CheckboxContainer isVideoWatched={isVideoWatched}>
          <Checkbox>
            <img
              onClick={() => handleCheckbox('accept')}
              src={
                registrant?.videoStatus === 'accept'
                  ? checkboxGreenChecked
                  : checkboxGreen
              }
              alt="green-checkbox"
            />
            {intl.formatMessage({ id: 'ACCEPT' })}
          </Checkbox>
          <Checkbox>
            <img
              onClick={() => handleCheckbox('reject')}
              src={
                registrant?.videoStatus === 'reject'
                  ? checkboxRedChecked
                  : checkboxRed
              }
              alt="red-checkbox"
            />
            {intl.formatMessage({ id: 'REJECT' })}
          </Checkbox>
        </CheckboxContainer>
      </LineContainer>
    </Container>
  );
}

export default RegistrantVideo;
